import React, { memo } from 'react';
import { BotIcon, CalendarDaysIcon, CheckIcon, MessageCircleIcon, TablePropertiesIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
const pains = ["Te cancelan con un 'no puedo hoy' 10 minutos antes. Perdés la hora, el viaje, la energía — y ni le cobrás.", 'Das una clase grupal y ves a alguien mirando el celular mientras explicás el ejercicio.', "Le proponés seguir el mes que viene y te dice 'después te aviso' — y te deja en visto.", 'Armás una rutina personalizada con dedicación, y ni la mira.', "Esperás a un alumno nuevo que 'confirmó' y nunca aparece. Ni un mensaje.", 'Preparás la clase, llegás antes, y viene la mitad de los que confirmaron.', 'Un alumno te regatea el precio después de darle resultados reales.', "Termina el mes y volvés a mandar el mensaje de '¿seguimos?', rogando internamente que digan que sí."];
const modules = [['01', 'El Sistema de Captación de Clientes', 'La base: cómo estructurar tu negocio para que el próximo cliente no dependa de la suerte. Flujo completo, paso a paso.'], ['02', 'Instagram que Convierte', 'Cómo usar tu perfil para atraer alumnos que ya llegan interesados — sin necesitar miles de seguidores.'], ['03', 'WhatsApp que Cierra Ventas', 'Los guiones y la lógica detrás de cada conversación: desde el primer contacto hasta el sí.'], ['04', 'Multiplicá tus Canales de Venta', 'Más allá de Instagram: otras fuentes de clientes que la mayoría de los entrenadores ignora.'], ['05', 'Automatización e IA para Entrenadores', 'Herramientas concretas para que el seguimiento no dependa de tu memoria.'], ['06', 'Plan de Acción 90 Días', 'Día a día de implementación. Qué hacés la primera semana, el primer mes, el tercer mes.']];
const bonuses = [{
  icon: MessageCircleIcon,
  title: '50 Guiones de WhatsApp para Cerrar Clientes',
  description: "Respuestas listas para cada situación: el que pide descuento, el que dice 'lo pienso', el que desaparece.",
  value: '$9.000'
}, {
  icon: CalendarDaysIcon,
  title: 'Calendario de Contenidos por 90 Días',
  description: 'Qué publicar, cuándo y con qué objetivo. Tres meses de contenido planificado para Instagram.',
  value: '$12.000'
}, {
  icon: BotIcon,
  title: 'Biblioteca de Prompts de IA para Entrenadores',
  description: 'Los prompts exactos para que la IA te ayude con rutinas, copys y seguimientos — adaptados a entrenadores.',
  value: '$10.000'
}, {
  icon: TablePropertiesIcon,
  title: 'Plantillas de Seguimiento y Gestión de Clientes',
  description: 'El sistema para que ningún alumno se pierda y ninguna renovación se olvide.',
  value: '$8.000'
}];
export function ContentSections() {
  return <>
      <section className="border-y border-[#d4af37]/18 bg-card px-5 py-20 sm:px-8 sm:py-28" aria-label="Situaciones comunes de entrenadores" data-id="element-1">
        <div className="mx-auto max-w-6xl" data-id="element-2">
          <Reveal data-id="element-3">
            <SectionHeading centered eyebrow="Lo que nadie habla, pero todos vivimos" title="Si sos entrenador, alguno de estos momentos ya te pasó." data-id="element-4" />
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2" data-id="element-5">
            {pains.map((pain, index) => <Reveal key={pain} delay={index * 0.035} data-id="element-6">
                <article className="border-l-[3px] border-gold bg-ink px-5 py-6 text-[15px] leading-7 text-ivory sm:px-6" data-id="element-7">
                  {pain}
                </article>
              </Reveal>)}
          </div>
          <Reveal data-id="element-8">
            <p className="mx-auto mt-14 max-w-xl text-center font-display text-3xl italic leading-[1.08] text-ivory sm:text-4xl" data-id="element-9">
              No es que no sepas entrenar.
              <br data-id="element-10" />
              Es que nadie te enseñó a{' '}
              <em className="font-normal text-gold" data-id="element-11">manejar esto.</em>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-8 sm:py-28" aria-label="El problema real" data-id="element-12">
        <div className="mx-auto max-w-[700px]" data-id="element-13">
          <Reveal data-id="element-14">
            <SectionHeading eyebrow="El problema real" title={<>
                  El boca a boca no es un sistema.
                  <br data-id="element-16" />
                  Es <em className="font-normal text-gold" data-id="element-17">una apuesta.</em>
                </>} data-id="element-15" />
          </Reveal>
          <Reveal delay={0.05} data-id="element-18">
            <div className="mt-8 space-y-6 text-lg leading-8 text-sage" data-id="element-19">
              <p data-id="element-20">
                Sabés entrenar. Tus alumnos mejoran. Pero dependés de que
                alguien te recomiende para que llegue el próximo. Cuando las
                recomendaciones se enfrían, la plata se cae. Y volvés a empezar
                de cero.
              </p>
              <p data-id="element-21">
                El Megapack Entrenadores te da el sistema que falta: cómo usar
                Instagram y WhatsApp para que los clientes lleguen a vos, no al
                revés. Sin pagar ads. Sin actuar. Sin depender de la suerte.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#d4af37]/18 bg-card px-5 py-20 sm:px-8 sm:py-28" aria-label="Contenido del sistema" data-id="element-22">
        <div className="mx-auto max-w-6xl" data-id="element-23">
          <Reveal data-id="element-24">
            <SectionHeading centered eyebrow="Contenido del sistema" title="6 módulos. Un solo sistema." description="Cada módulo construye sobre el anterior. Al final de los 90 días, tenés el sistema activo." data-id="element-25" />
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-26">
            {modules.map(([number, title, description], index) => <Reveal key={number} delay={index * 0.04} data-id="element-27">
                <article className="premium-card h-full bg-ink p-6" data-id="element-28">
                  <span className="font-mono text-xs font-bold text-gold" data-id="element-29">
                    {number}
                  </span>
                  <h3 className="mt-8 font-display text-3xl font-bold leading-[0.98] tracking-[-0.025em] text-ivory" data-id="element-30">
                    {title}
                  </h3>
                  <p className="mt-5 text-sm leading-6 text-sage" data-id="element-31">
                    {description}
                  </p>
                </article>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-8 sm:py-28" aria-label="Bonos incluidos" data-id="element-32">
        <div className="mx-auto max-w-6xl" data-id="element-33">
          <Reveal data-id="element-34">
            <SectionHeading centered eyebrow="Bonos — incluidos gratis" title="Cuatro herramientas que aceleran el resultado." data-id="element-35" />
            <p className="mt-6 text-center font-mono text-sm text-sage" data-id="element-36">
              <span className="text-gold-dark line-through" data-id="element-37">$39.000 ARS</span>{' '}
              <span className="ml-2 font-semibold text-gold" data-id="element-38">
                → gratis con el sistema
              </span>
            </p>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2" data-id="element-39">
            {bonuses.map(({
            icon: Icon,
            title,
            description,
            value
          }, index) => <Reveal key={title} delay={index * 0.05} data-id="element-40">
                <article className="premium-card h-full bg-card p-7" data-id="element-41">
                  <Icon className="text-gold" size={23} strokeWidth={1.5} aria-hidden="true" data-id="element-42" />
                  <h3 className="mt-7 font-display text-3xl font-bold leading-none text-ivory" data-id="element-43">
                    {title}
                  </h3>
                  <p className="mt-4 max-w-lg text-sm leading-6 text-sage" data-id="element-44">
                    {description}
                  </p>
                  <p className="mt-7 font-mono text-sm font-semibold text-gold" data-id="element-45">
                    {value}
                  </p>
                </article>
              </Reveal>)}
          </div>
        </div>
      </section>
    </>;
}